/**
 * PMO フェーズリードタイム型定義
 *
 * 案件が要件定義にアサインされてから結合テスト完了に到達するまでを区間に割り、
 * 区間ごとの所要日数（分位数）と計測可否を表す API 契約型。
 * Source of truth はバックエンド (`GET /kpi/pmo-flow/*`) のスキーマで、
 * 変更時は同期させること。共有ドメイン型 (`KpiTeam` 等) は広げず、この画面専用に閉じる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイム画面のAPI契約型。区間キー・区間区分・計測可否・分位数・規模帯・サマリー/トレンド/規模別リードタイムのレスポンスを定義
 * @graph-connects none
 */

/**
 * フェーズ区間キー。工程の並びは
 * 要件定義assign → 要件定義完了 → assign → 着手 → 開発完了 → 結合テスト着手 →
 * 結合テスト完了 → デザインチェック。
 * 要件定義 2 区間は 2026-10 の開発フロー変更で入った工程で、開発 assign より**前**。
 * デザインチェックは結合テストの**後**の関門。
 */
export type PmoFlowSegmentKey =
  | 'reqAssignToReqDone'
  | 'reqDoneToAssign'
  | 'assignToStart'
  | 'startToDevDone'
  | 'devDoneToTestStart'
  | 'testExecution'
  | 'integDoneToDesignCheck'
  | 'designCheck';

/**
 * 集計母集団の切り口。デザイン工程の有無で構成が根本的に違うため分けて描く。
 * `withDesign` はデザインチェック子チケットを持つ母票。
 */
export type PmoFlowCohortKey = 'withDesign' | 'devOnly';

/**
 * 区間の性質。requirement=要件定義工程 / wait=待ち時間 / work=作業時間 / mixed=両方を含む。
 * `requirement` は開発 assign より前の工程で、別予算・別担当で回るため開発フェーズの
 * 待ち / 作業とは独立した区分にする。
 */
export type PmoFlowSegmentClass = 'requirement' | 'wait' | 'work' | 'mixed';

/**
 * 区間の計測可否。
 * - `measurable`: 実データで計測できている
 * - `pending`: ステータスイベント収集パイプラインが未整備で計測できない
 * - `sparse`: 計測はできるがサンプル数が少なく参考値
 */
export type PmoFlowAvailability = 'measurable' | 'pending' | 'sparse';

/** 分位数（日数 or 日数/pt）。 */
export interface PmoFlowQuantiles {
  p25: number;
  p50: number;
  p75: number;
  p90: number;
}

/** 区間 1 件分の統計。計測できない区間は days / daysPerPoint が null で pendingReason を持つ。 */
export interface PmoFlowSegmentStat {
  key: PmoFlowSegmentKey;
  segmentClass: PmoFlowSegmentClass;
  availability: PmoFlowAvailability;
  count: number;
  days: PmoFlowQuantiles | null;
  daysPerPoint: PmoFlowQuantiles | null;
  pendingReason: string | null;
}

/**
 * 案件 1 件が「止まっていた」営業日の合計。区間別の分位数は案件をまたいで足せないため、
 * バックエンドが案件単位で合算してから分位数を取っている。
 *
 * **主指標は p90**。待ちは右裾が長く、実測で p50 10 日に対し p90 54 日。
 * `sharePercent` は構造の説明用で、改善指標には使わない（分母が一緒に動くため）。
 */
export interface PmoFlowWaitTotal {
  count: number;
  days: PmoFlowQuantiles;
  /** 全体リードタイムに占める待ちの割合（%）の中央値 */
  sharePercent: number | null;
}

/**
 * 案件 1 件が実際に手を動かしていた営業日の合計（開発 + 結合テスト実施）。
 *
 * **規模正規化はこの粒度でしか成立しない。** pt は案件全体の開発見積なので、
 * 単一区間の所要日数で割ると「その工程の速さ」ではなく「案件の大きさ」を測ってしまう
 * （実測で結合テスト実施の pt/日 は案件サイズと 0.279〜0.695 相関するが、
 * 作業を合算すると 0.011〜0.033 まで落ちる）。
 */
export interface PmoFlowWorkTotal {
  count: number;
  availability: PmoFlowAvailability;
  days: PmoFlowQuantiles;
  daysPerPoint: PmoFlowQuantiles | null;
}

/**
 * 案件 1 件が assign から結合テスト完了までに 1 営業日あたり産出した実績 pt。
 *
 * 作業合計と対になる指標で、**分母が違う**。作業合計は手を動かしていた営業日だけを
 * 分母にするが、こちらは経過営業日をそのまま使うので待ちで止まっていた時間も含む。
 * 作業が速くなっても待ちが伸びればこの値は上がらない。
 * 分子も見積ではなく実績 pt（産出量の指標なので見積で割ると計画を測ることになる）。
 *
 * `daysPerPoint` は API の生値（営業日/実績pt）で、表示は pt/日 へ反転する。
 */
export interface PmoFlowProjectTotal {
  count: number;
  availability: PmoFlowAvailability;
  days: PmoFlowQuantiles;
  daysPerPoint: PmoFlowQuantiles | null;
}

/** 母集団1つ分のフェーズ構成。`devOnly` にはデザイン工程の区間が含まれない。 */
export interface PmoFlowCohortSummary {
  key: PmoFlowCohortKey;
  usableCount: number;
  segments: PmoFlowSegmentStat[];
  totalLeadTime: PmoFlowQuantiles | null;
  waitTotal: PmoFlowWaitTotal | null;
  workTotal: PmoFlowWorkTotal | null;
  projectTotal: PmoFlowProjectTotal | null;
}

/** `GET /kpi/pmo-flow/summary` のレスポンス。 */
export interface PmoFlowSummaryResponse {
  segments: PmoFlowSegmentStat[];
  totalLeadTime: PmoFlowQuantiles | null;
  cohorts: PmoFlowCohortSummary[];
  waitTotal: PmoFlowWaitTotal | null;
  workTotal: PmoFlowWorkTotal | null;
  projectTotal: PmoFlowProjectTotal | null;
  months: number;
}

/** 月次コホート 1 点分のトレンド値。 */
export interface PmoFlowTrendPoint {
  /** 'YYYY-MM' 形式のコホート月 */
  cohortMonth: string;
  count: number;
  p50Days: number;
  p90Days: number;
  p50DaysPerPoint: number | null;
}

/** 案件あたりの待ち合計を指す擬似セグメントキー。 @graph-connects none */
export const PMO_FLOW_WAIT_TOTAL_TARGET = 'waitTotal';

/** トレンドで選べる対象。実在する区間に加えて待ち合計を選べる。 */
export type PmoFlowTrendTarget = PmoFlowSegmentKey | typeof PMO_FLOW_WAIT_TOTAL_TARGET;

/** `GET /kpi/pmo-flow/trend` のレスポンス。 */
export interface PmoFlowTrendResponse {
  segment: PmoFlowTrendTarget;
  points: PmoFlowTrendPoint[];
  months: number;
}

/** `GET /kpi/pmo-flow/summary` のクエリパラメータ。 */
export interface PmoFlowSummaryParams {
  months?: number;
}

/** `GET /kpi/pmo-flow/trend` のクエリパラメータ。segment は必須。 */
export interface PmoFlowTrendParams {
  segment: PmoFlowTrendTarget;
  months?: number;
}

/** 充足率の分子内訳。件数と pt を両方持ち、見積欠損を UI で明示できるようにする。 */
export interface PmoSupplyRateNumerator {
  readyProjectCount: number;
  readyProjectPoints: number;
  missingEstimateCount: number;
  lightTaskCount: number;
  lightTaskPoints: number;
  totalPoints: number;
}

/** 分母を構成する月ごとの取り分。先2週間が月をまたぐと2要素になる。 */
export interface PmoSupplyRateDenominatorSegment {
  /** 'YYYY-MM' 形式（JST） */
  month: string;
  /** この月の予算から取る週数 */
  weeks: number;
  /** その月の予算pt（fibo予算 PDG + ACE − Mallメンバー分）。シート未作成なら null */
  monthlyBudgetPoints: number | null;
  points: number | null;
}

/** 充足率の分母内訳。対象月の fibo予算が未作成なら points が null になる。 */
export interface PmoSupplyRateDenominator {
  /** 先読みする週数（先2週間） */
  weeks: number;
  /** 週割りに使う1ヶ月あたりの週数 */
  weeksPerMonth: number;
  segments: PmoSupplyRateDenominatorSegment[];
  points: number | null;
}

/**
 * 要件定義フェーズ 1 段分の在庫。
 * 開発ざっくり見積が入っていて、要件定義工程の子票（業務フロー図作成 / 機能一覧作成 /
 * バリデーションリスト作成 / 総合テスト作成 / 要件定義レビュー）が全て未対応かつ、そのうち 1 件以上が
 * 2026-10 以降に作成され、開発アサインが未確定の母票。
 * 業務フロー図作成の子票の担当者の有無で 2 段に割る。
 * 計上 pt はざっくり見積平均 × `requirementPointRatio`（15%）。
 */
export interface PmoSupplyRateRequirementStage {
  /** 対象の母票数 */
  projectCount: number;
  /** 上記母票のざっくり見積平均 × 15% の合計 pt */
  points: number;
}

/** `GET /kpi/pmo-flow/supply-rate` のレスポンス。supplyRate は百分率、算出不可なら null。 */
/**
 * 開発アサインは確定したが、まだ着手していない在庫（開発着手予定）。
 * 母票の種別がアサイン後（プロダクト一覧 / 潜在バグ / 案件起因バグ）で、ステータスが未対応のまま、
 * アサイン確定の証跡（開発リーダー / アサイン決定日 / アサイン状況=決定）を 2 つ以上持ち、
 * 事業がエアクロモール以外で、ざっくり見積が入っているもの。
 * 見積必須のため見積欠損は構造上発生しない。
 */
export interface PmoSupplyRatePlannedStart {
  /** アサイン済み・未着手の案件数 */
  projectCount: number;
  /** 上記案件のざっくり見積の合計 pt */
  points: number;
}

export interface PmoSupplyRateResponse {
  numerator: PmoSupplyRateNumerator;
  requirementAssignable: PmoSupplyRateRequirementStage;
  requirementPlannedStart: PmoSupplyRateRequirementStage;
  plannedStart: PmoSupplyRatePlannedStart;
  denominator: PmoSupplyRateDenominator;
  /** 開発アサイン可能の充足率（%） */
  supplyRate: number | null;
  /** 開発着手予定の充足率（%）。分母が取れないなら null */
  plannedStartSupplyRate: number | null;
  /** 要件定義アサイン可能の充足率（%）。分母が取れないなら null */
  requirementAssignableSupplyRate: number | null;
  /** 要件定義着手予定の充足率（%）。分母が取れないなら null */
  requirementPlannedStartSupplyRate: number | null;
  /** 分子に含める「アサイン前」母票の起票遡及日数（開発アサイン待ちの 2 段にのみ掛かる） */
  recentDays: number;
  /** 軽タスク 1 件あたりの換算 pt */
  lightTaskPointPerTask: number;
  /** 要件定義 pt の換算率（ざっくり見積平均に対する割合。0.15 = 15%） */
  requirementPointRatio: number;
}

/**
 * 集計対象の段（漏斗の上流から順）。
 * - `requirementAssignable`:   見積あり・要件定義子票が全て未対応かつ2026-10以降作成・開発アサイン未確定で、業務フロー図作成に担当者が付いていない
 * - `requirementPlannedStart`: 上と同じ状態で、業務フロー図作成に担当者が付いている（未着手）
 * - `assignable`:              総合テスト作成が完了した案件と、見積あり・DLE Reviewed 済みで未アサインのバグ（今すぐ開発アサインできるもの）
 * - `plannedStart`:            開発アサインは確定したが、まだ着手していない案件（見積あり・モール以外）
 */
export type PmoSupplyRateStage =
  | 'requirementAssignable'
  | 'requirementPlannedStart'
  | 'assignable'
  | 'plannedStart';

/**
 * 要否カスタムフィールドで制御される準備子票（業務フロー図作成 / バリデーションリスト作成）の状況。
 * 子票の有無だけでは「まだ作っていない」のか「要らない」のか区別できないため要否を併せ持つ。
 */
export interface PmoSupplyRatePrepRequirement {
  /** `unknown` は要否カスタムフィールドが未設定 */
  requirement: 'required' | 'notRequired' | 'unknown';
  /** 必要な案件の子票ステータス。不要・未設定・子票なしは null */
  status: string | null;
}

/**
 * 開発リーダーの所属で決まるプール区分。
 *
 * 「着手予定」の在庫が どのチームの枠を埋めているか を見るための区分で、判定材料は母票の
 * 開発リーダー 1 つ。開発リーダーには各チームの teamlead / BSE（airCloset は Hiro、
 * Platform は Yukito）かそのチームの DEV が入るため、そのニックネームの所属がそのまま
 * 案件の所属になる。`other` は 未設定・名簿外・上記以外の所属（Mall / QA 等）の受け皿。
 */
export type PmoSupplyRateDevLeaderTeam = 'aircloset' | 'platform' | 'pdg' | 'other';

/** ドリルダウン用の 1 件（案件。アサイン可能段ではバグも含む）。見積未入力の案件は roughPoint が null（バグは見積あり必須のため null にならない）。 */
export interface PmoSupplyRateProject {
  issueKey: string;
  summary: string;
  status: string | null;
  business: string | null;
  responsibleTeam: string | null;
  /** 母票の開発リーダー（ニックネーム）。未設定なら null */
  devLeader: string | null;
  /** 開発リーダーの所属から解決したプール区分 */
  devLeaderTeam: PmoSupplyRateDevLeaderTeam;
  /** 事業が エアクロメンズ か。チーム別プールの中で更にメンズ分を切り出すのに使う */
  isMensBusiness: boolean;
  roughPointMin: number | null;
  roughPointMax: number | null;
  roughPoint: number | null;
  /** その段が分子へ積む計上 pt（開発 2 段は見積平均、要件定義 2 段はその 15%） */
  countedPoint: number | null;
  createdAt: string | null;
  businessFlow: PmoSupplyRatePrepRequirement;
  validationList: PmoSupplyRatePrepRequirement;
}

/** `GET /kpi/pmo-flow/supply-rate/projects` のレスポンス。 */
export interface PmoSupplyRateProjectsResponse {
  projects: PmoSupplyRateProject[];
}

/**
 * 実績 pt の規模帯キー。境界は重ならない
 * （`pt51To100` は 50pt 超 100pt 以下、`ptOver100` は 100pt 超）。
 */
export type PmoScaleBandKey = 'pt1To50' | 'pt51To100' | 'ptOver100';

/** ドリルダウンの対象。3 つの規模帯に加えて「全体」を選べる。 */
export type PmoScaleScopeKey = PmoScaleBandKey | 'overall';

/** 規模帯 1 件分のリードタイム分位数（営業日）。 */
export interface PmoScaleLeadTimeQuantiles {
  p50: number;
  p90: number;
}

/** 規模帯 1 件分の統計。対象 0 件なら `days` は null・`distribution` は空配列。 */
export interface PmoScaleLeadTimeBand {
  key: PmoScaleBandKey;
  count: number;
  /**
   * この帯の実績 pt の平均（小数第1位）。0 件なら null。
   * 帯は幅を持つ（1〜50pt に 3pt も 48pt も入る）ので、p50 / p90 の日数が
   * どれくらいの規模に対する値なのかを読むために併せて出す。
   */
  averagePoint: number | null;
  days: PmoScaleLeadTimeQuantiles | null;
  /**
   * p0〜p100 の営業日（添字がそのまま分位、101 要素）。`days` はこの配列の 50 / 90 番目。
   * カードは p50 / p90 だけ出し、この配列はドリルダウンの分布表示で使う。
   */
  distribution: number[];
}

/**
 * 規模を問わない全体の統計。**3 帯の和**であって、実績 pt が 1 未満で帯に入らなかった母票は
 * 含まない（含めると「全体の件数 = 3 帯の合計」が画面上で成り立たなくなる）。
 * 分位数は帯の値から合成せず、母集団をまとめ直してサーバー側で取り直している。
 */
export interface PmoScaleOverallStat {
  key: 'overall';
  count: number;
  /** 全体の実績 pt の平均（小数第1位）。0 件なら null */
  averagePoint: number | null;
  days: PmoScaleLeadTimeQuantiles | null;
  distribution: number[];
}

/** ドリルダウンできる単位（規模帯 or 全体）。持つ値も見せ方も同じなので型で束ねる。 */
export type PmoScaleScopeStat = PmoScaleLeadTimeBand | PmoScaleOverallStat;

/** 「直近半年をまとめて見る」を指す月キー。 @graph-connects none */
export const PMO_SCALE_ALL_MONTHS_KEY = 'all';

/**
 * 遅い側の案件を母集団から外す指定。`none` は除外なし。
 * しきい値は**規模帯ごと**に取る（全体で 1 本にすると 100pt 超の帯だけが丸ごと落ちる）。
 */
export type PmoScaleExcludeMode = 'none' | 'p75' | 'p90';

/**
 * 尾側明細で選べるしきい値の分位。p90 だけだと母数の小さい帯で数件しか並ばないので、
 * 「伸びた側 1/4」まで下ろせる p75 も用意する。
 */
export type PmoScaleTailPercentile = 75 | 90;

/**
 * `GET /kpi/pmo-flow/scale-lead-time` のレスポンス。
 * リリース完了月ごとに集計し、画面上部の月数セレクトには連動しない。
 */
export interface PmoScaleLeadTimeResponse {
  overall: PmoScaleOverallStat;
  bands: PmoScaleLeadTimeBand[];
  totalCount: number;
  /** 実績 pt が 1 未満で帯に入れなかった母票数 */
  excludedNoPointCount: number;
  /** 選択できる月数（`availableMonths` の長さ） */
  months: number;
  /**
   * 実際に集計したリリース完了月（`YYYY-MM`）。まとめ表示なら `all`。
   * 要求した月ではなくサーバーが集計した月なので、セレクトの表示はこちらに合わせる。
   */
  month: string;
  /** 選択できるリリース完了月（新しい順） */
  availableMonths: string[];
  /**
   * 適用した除外モード。`none` 以外なら件数・平均 pt・分位はすべて
   * 除外後の母集団で計算し直した値。
   */
  excludeMode: PmoScaleExcludeMode;
}

/**
 * 規模帯の尾側（選択した分位以上）に落ちた案件 1 件。
 * 分位は「どれだけ伸びうるか」までしか教えてくれないので、何を直すかを見るために明細を持つ。
 */
export interface PmoScaleTailProject {
  issueKey: string;
  summary: string;
  responsibleTeam: string | null;
  devLeader: string | null;
  actualPoint: number;
  /** YYYY-MM-DD */
  startDate: string | null;
  /** YYYY-MM-DD */
  releasedDate: string | null;
  /** 開始日→リリース完了日 の営業日 */
  days: number;
}

/** `GET /kpi/pmo-flow/scale-lead-time/projects` のレスポンス。 */
export interface PmoScaleTailProjectsResponse {
  scope: PmoScaleScopeKey;
  /** 抽出に使った分位（75 / 90） */
  percentile: PmoScaleTailPercentile;
  /** 対象にしたリリース完了月（`YYYY-MM`）。`all` はまとめ表示 */
  month: string;
  /** 適用した除外モード。カードと同じ母集団から抽出したことを示す */
  excludeMode: PmoScaleExcludeMode;
  /**
   * 抽出に使った分位の営業日。サマリーから渡すのではなく明細クエリ側で計算した値なので、
   * 画面に出るしきい値と一覧の中身は必ず一致する。0 件なら null。
   */
  thresholdDays: number | null;
  /** 営業日の長い順 */
  projects: PmoScaleTailProject[];
}
